<template>
  <sc-flex :gap="12">
    <aside class="posthog-recordings-list">
      <sc-flex :is-y-centered="true" :gap="8">
        <input v-model="search" placeholder="Search" />
        <sc-button-group v-if="!!search || selectedRecordingId">
          <sc-button
            size="small"
            type="secondary"
            icon-left="close"
            :is-icon-only="true"
            @click="onClearSearch"
          ></sc-button>
        </sc-button-group>
      </sc-flex>
      <sc-icon v-if="isLoading" name="loading" size="large"></sc-icon>
      <ol v-else-if="recordings" class="recodingItems">
        <li v-if="recordings.length === 0">No recordings yet</li>
        <li
          v-else
          v-for="(recording, i) in recordings"
          :key="i"
          @click="onSelectRecording(recording.id)"
        >
          <div
            class="recordingItem"
            :class="
              selectedRecordingId === recording.id
                ? 'recordingItem--active'
                : ''
            "
          >
            <sc-small
              ><b>{{ getRecordingName(recording) }}</b></sc-small
            >
            <sc-tiny>{{ moment(recording.created_at).fromNow() }}</sc-tiny>
          </div>
        </li>
      </ol>
    </aside>
    <sc-flex v-if="isRecordingLoading" :padding="16">
      <sc-icon name="loading" size="large"></sc-icon>
    </sc-flex>
    <sc-flex
      v-else-if="hasValidRecording"
      :is-column="true"
      :gap="24"
      :padding="16"
    >
      <sc-card v-if="events && events.length < 2">
        <sc-flex :gap="4" :is-y-centered="true">
          <sc-icon name="warning-circle" size="normal"></sc-icon>
          <sc-small>Web player needs 2 separate snapshots to play.</sc-small>
        </sc-flex>
      </sc-card>
      <RrWebPlayer v-else-if="events" :events="events" />
      <sc-card v-if="hasValidRecording">
        <sc-small>
          <b>
            {{ selectedRecording.person.data.name }}
          </b>
        </sc-small>
        <sc-flex :is-y-centered="true" :gap="8">
          <sc-small> uuid: {{ selectedRecording.person.data.uuid }} </sc-small>
          <sc-small>&ndash;</sc-small>
          <sc-small>
            {{ moment(selectedRecording.person.created_at).fromNow() }}
          </sc-small>
          <sc-small>&ndash;</sc-small>
          <sc-small> {{ events ? events.length : 0 }} events </sc-small>
        </sc-flex>
      </sc-card>
    </sc-flex>
    <sc-flex v-else :is-y-centered="true" :gap="8" :padding="16">
      <sc-icon name="na" size="normal"></sc-icon>
      <sc-p>Select a recording from the list</sc-p>
    </sc-flex>
  </sc-flex>
</template>

<script>
import API from '@/api';
import moment from 'moment';
import RrWebPlayer from '@preflight-hq/rrweb-player-vue';

export default {
  name: 'PosthogReplay',
  components: {
    RrWebPlayer,
  },
  data() {
    return {
      events: [{}, {}],
      search: '',
      isLoading: true,
      isRecordingLoading: false,
      recordings: [],
      selectedRecording: null,
      selectedRecordingId: null,
    };
  },
  computed: {
    hasValidRecording() {
      return Boolean(
        this.selectedRecording &&
          this.selectedRecording.person &&
          this.selectedRecording.person.data &&
          this.selectedRecording.person.data.name,
      );
    },
  },
  methods: {
    moment,
    async getPosthogSessionRecordings(newSearch = '') {
      const searchName = (this.search || newSearch).toLowerCase();
      const { data } = await API.posthog.getPosthogSessionRecordings(
        searchName,
      );

      this.recordings = data;
      this.isLoading = false;
    },
    async onSelectRecording(id) {
      this.isRecordingLoading = true;
      this.selectedRecordingId = id;

      const { data } = await API.posthog.getPosthogSessionRecording(id);

      this.events = this.getEvents(data);
      this.selectedRecording = data;
      this.isRecordingLoading = false;
    },
    onClearSearch() {
      this.search = '';
      this.selectedRecording = null;
      this.selectedRecordingId = null;
    },
    getEvents(newEvents) {
      const snapshots = newEvents.snapshots
        .map(s => s.data)
        .flat()
        .map(snapshot => {
          const isString = typeof snapshot === 'string';
          let mergedData = [];

          if (isString) {
            try {
              const parsedSnapshot =
                '[' +
                snapshot
                  .split('\n')
                  .join(',')
                  .slice(0, -1) +
                ']';
              const cleanSnapshot = JSON.parse(parsedSnapshot);

              mergedData = cleanSnapshot.reduce(
                (a, item) => a.concat(item.data),
                [],
              );
              console.debug({ mergedData, cleanSnapshot });
            } catch (error) {
              console.error(error);
            }
          }

          return mergedData;
        });

      return snapshots.flat();
    },
    getRecordingName(recording) {
      const hasRecording = recording && recording.posthog_id;
      const name =
        hasRecording &&
        recording.person &&
        recording.person.data &&
        recording.person.data.name;

      if (!hasRecording) return '';
      if (name) return name;

      return recording.posthog_id.toString().substring(0, 6);
    },
  },
  async created() {
    this.getPosthogSessionRecordings();
  },
  watch: {
    search: {
      async handler(value) {
        this.getPosthogSessionRecordings(value);
      },
    },
  },
};
</script>

<style lang="css" scoped="true">
ol li,
ol {
  padding: 0;
  margin: 0;
  list-style-type: none !important;
}

.posthog-recordings-list {
  position: sticky;
  top: 0;

  padding: 12px;
  display: flex;
  gap: 16px;
  flex-direction: column;
  height: calc(100dvh - 64px);
  min-width: 260px;
  overflow: auto;
  width: 260px;

  background-color: var(--gray-0);

  cursor: pointer;
}

.posthog-recordings-list input {
  height: 32px;
  min-height: 32px;
  padding: 0 12px;
  width: 100%;

  background-color: var(--gray-0);
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  outline: none;
}

.recodingItems {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.recordingItem {
  display: flex;
  flex-direction: column;
  padding: 8px;

  background-color: var(--gray-0);
  border-radius: 8px;
  transition: all 80ms ease-in-out;
}

.recordingItem:hover {
  background-color: var(--gray-100);
}

.recordingItem--active {
  background-color: var(--gray-300);
}
</style>
